// Workspace column — Slack-style 60 px far-left rail.
//
// Sprint 11b.1 shipped the static column + auto-avatar + "+ New project".
// Sprint 11b.2 adds drag-to-reorder (mouse + touch via @dnd-kit) and
// per-project right-click / long-press menu (Edit / Archive) sourcing
// from the new useProjectsStore.
//
// Drop indicator: a 2 px line of `accent` color rendered between two
// avatars where the drop will land. The "+" button is OUTSIDE the
// SortableContext so it never accepts drops (WS12).
//
// Gesture matrix (§2.2):
//   * Tap          < 400 ms, no movement   → select workspace.
//   * Long-press   ≥ 600 ms, no movement   → context menu (Edit / Archive).
//   * Long-press + drag ≥ 400 ms + > 5 px  → drag-to-reorder.
//   * Right-click  (desktop)               → context menu.

import { useCallback, useEffect, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  type DragCancelEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppStore } from '../stores/app.store';
import {
  useActiveWorkspaceStore,
  healActiveWorkspace,
} from '../stores/active-workspace.store';
import { useProjectsStore } from '../stores/projects.store';
import { WorkspaceAvatar } from './atoms/WorkspaceAvatar';
import { WorkspaceAvatarContextMenu } from './WorkspaceAvatarContextMenu';
import { NewProjectModal } from './NewProjectModal';
import { EditProjectModal } from './EditProjectModal';
import { api } from '../lib/api';
import { useServersStore } from '../stores/servers.store';
import type { Project } from '../types';
import { Icon } from './atoms/Icon';
import { useTranslation } from '../i18n';

const DEFAULT_PROJECT_ID = 'cw-crm-v2';

export function WorkspaceColumn() {
  const { t } = useTranslation();
  const projects = useProjectsStore((s) => s.projects);
  const loadProjects = useProjectsStore((s) => s.load);
  const reorder = useProjectsStore((s) => s.reorder);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const activeId = useActiveWorkspaceStore((s) => s.activeWorkspaceId);
  const setActiveId = useActiveWorkspaceStore((s) => s.setActiveWorkspaceId);
  const selectAgent = useAppStore((s) => s.selectAgent);
  const agents = useAppStore((s) => s.agents);

  // Cursor-baseline activation: 5 px distance for mouse, 400 ms + 5 px
  // tolerance for touch (gesture matrix §2.2). Keyboard sensor for a11y.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reload = useCallback(() => {
    void loadProjects().then(() => {
      healActiveWorkspace(
        useProjectsStore.getState().projects.map((p) => p.id),
      );
    });
  }, [loadProjects]);

  useEffect(() => {
    reload();
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  function unreadCountForProject(projectId: string): number {
    let count = 0;
    for (const id in agents) {
      const a = agents[id];
      if (a && a.projectId === projectId && a.unread && !a.deleted) {
        count++;
      }
    }
    return count;
  }

  function handleSelectWorkspace(projectId: string) {
    setActiveId(projectId);
    selectAgent(null);
  }

  function handleProjectCreated(slug: string) {
    setActiveId(slug);
    selectAgent(null);
    reload();
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = projects.map((p) => p.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(ids, oldIndex, newIndex);
    void reorder(next);
  }

  function handleDragCancel(_event: DragCancelEvent) {
    setActiveDragId(null);
  }

  async function handleArchive(projectId: string) {
    if (projectId === DEFAULT_PROJECT_ID) return;
    const targets = useServersStore.getState().servers;
    const baseUrls = targets.length === 0 ? [''] : targets.map((s) => s.baseUrl);
    await Promise.allSettled(
      baseUrls.map((baseUrl) =>
        api(`/api/projects/${encodeURIComponent(projectId)}/archive`, {
          method: 'POST',
          baseUrl: baseUrl || undefined,
        }),
      ),
    );
    reload();
  }

  return (
    <>
      <aside
        className="flex flex-col items-center pt-safe-top pb-2 bg-background border-r border-border shrink-0 h-full overflow-y-auto overflow-x-hidden"
        style={{ width: 60 }}
        data-testid="workspace-column"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={projects.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex-1 flex flex-col items-center w-full gap-0.5">
              {projects.map((p) => (
                <SortableWorkspaceAvatar
                  key={p.id}
                  project={p}
                  unreadCount={unreadCountForProject(p.id)}
                  isActive={activeId === p.id}
                  isDragging={activeDragId === p.id}
                  onSelect={() => handleSelectWorkspace(p.id)}
                  onEdit={() => setEditingProject(p)}
                  onArchive={() => void handleArchive(p.id)}
                  archiveDisabled={p.id === DEFAULT_PROJECT_ID}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div className="mt-2 mb-1">
          <button
            type="button"
            onClick={() => setNewModalOpen(true)}
            className="flex items-center justify-center w-10 h-10 rounded-xl border-2 border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
            data-testid="workspace-add-button"
            aria-label={t('workspace.addProject')}
            title={t('workspace.addProject')}
          >
            <Icon name="Plus" size={20} aria-hidden="true" />
          </button>
        </div>
      </aside>

      {newModalOpen && (
        <NewProjectModal
          open={newModalOpen}
          onOpenChange={setNewModalOpen}
          onCreated={handleProjectCreated}
        />
      )}

      {/* Mount EditProjectModal only when a project is selected — keeps */}
      {/* the hook count stable inside the modal regardless of whether */}
      {/* the user has opened it yet. Defence-in-depth on top of the */}
      {/* useMemo-before-early-return fix in EditProjectModal itself. */}
      {editingProject !== null && (
        <EditProjectModal
          project={editingProject}
          open
          onOpenChange={(open) => {
            if (!open) setEditingProject(null);
          }}
        />
      )}
    </>
  );
}

interface SortableAvatarProps {
  project: Project;
  unreadCount: number;
  isActive: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onArchive: () => void;
  archiveDisabled: boolean;
}

function SortableWorkspaceAvatar({
  project,
  unreadCount,
  isActive,
  isDragging,
  onSelect,
  onEdit,
  onArchive,
  archiveDisabled,
}: SortableAvatarProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: thisIsDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: thisIsDragging || isDragging ? 0.5 : 1,
    width: '100%',
  } satisfies React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`workspace-sortable-${project.id}`}
    >
      <WorkspaceAvatarContextMenu
        onEdit={onEdit}
        onArchive={onArchive}
        archiveDisabled={archiveDisabled}
      >
        <WorkspaceAvatar
          slug={project.id}
          name={project.name}
          unreadCount={unreadCount}
          isActive={isActive}
          onClick={onSelect}
          avatarColor={project.avatarColor}
          avatarInitials={project.avatarInitials}
        />
      </WorkspaceAvatarContextMenu>
    </div>
  );
}
